import { getEffectivePlan } from "../../platform/util/utils";

/**
 * @param {any} providerUser
 * @param {boolean | null | undefined} [isCareBusiness]
 */
export function hasStandaloneBookingAccess(providerUser, isCareBusiness = null) {
  const explicit = providerUser?.productAccess?.booking?.standalone;
  if (typeof explicit === "boolean") return explicit;
  const subscribed = providerUser?.productAccess?.booking?.subscribed;
  if (typeof subscribed === "boolean") return subscribed;
  if (typeof isCareBusiness === "boolean") return !isCareBusiness;
  return true;
}

export function getProductPlanTierCode(subscription, plans = []) {
  const planId = subscription?.planId;
  if (!planId || !Array.isArray(plans)) return "FREE";
  const matchedPlan = plans.find(
    (plan) => String(plan?.id) === String(planId)
  );
  const rawCode = String(matchedPlan?.code || "").toUpperCase();
  if (rawCode.includes("CAREJOBS_STANDARD")) return "STANDARD";
  if (rawCode.includes("CAREJOBS_PREMIUM")) return "PREMIUM";
  if (rawCode.includes("ENTERPRISE")) return "ENTERPRISE";
  if (rawCode.includes("PREMIUM")) return "PREMIUM";
  if (rawCode.includes("STANDARD")) return "STANDARD";
  if (rawCode.includes("FREE")) return "FREE";
  return "FREE";
}

export function hasPaidBookingFeatures(
  providerUser,
  bookingSubscription = null,
  plans = [],
  isCareBusiness = null
) {
  if (!hasStandaloneBookingAccess(providerUser, isCareBusiness)) return false;
  const effectivePlan = Number(getEffectivePlan(providerUser) || 0);
  if (effectivePlan >= 2) return true;
  const explicitPaid = providerUser?.productAccess?.booking?.paid;
  if (typeof explicitPaid === "boolean") return explicitPaid;
  const tierCode = getProductPlanTierCode(bookingSubscription, plans);
  return (
    tierCode === "STANDARD" ||
    tierCode === "PREMIUM" ||
    tierCode === "ENTERPRISE"
  );
}

/**
 * @param {any} providerUser
 * @param {boolean | null | undefined} [isCareBusiness]
 */
export function hasCareWorkspaceAccess(providerUser, isCareBusiness = null) {
  const explicit = providerUser?.productAccess?.care?.enabled;
  if (typeof explicit === "boolean") return explicit;
  const subscribed = providerUser?.productAccess?.care?.subscribed;
  if (typeof subscribed === "boolean") return subscribed;
  if (typeof isCareBusiness === "boolean") return isCareBusiness;
  return false;
}

/**
 * @param {any} providerUser
 */
export function hasAiVoiceAccess(providerUser) {
  return !!providerUser?.productAccess?.addons?.aiVoice?.enabled;
}

/**
 * @param {any} providerUser
 * @param {string} productKey
 */
export function isProductSubscribed(providerUser, productKey) {
  const normalized = String(productKey || "").trim().toLowerCase();
  if (normalized === "care") {
    return !!providerUser?.productAccess?.care?.subscribed;
  }
  if (normalized === "booking") {
    return !!providerUser?.productAccess?.booking?.subscribed;
  }
  if (normalized === "aivoice") {
    return !!providerUser?.productAccess?.addons?.aiVoice?.subscribed;
  }
  return false;
}

/**
 * Pricing only needs a product switcher when the customer owns both primary
 * products. Single-product customers stay inside the product they entered.
 *
 * @param {any} providerUser
 */
export function hasMultiplePrimaryProductSubscriptions(providerUser) {
  return (
    isProductSubscribed(providerUser, "care") &&
    isProductSubscribed(providerUser, "booking")
  );
}

/**
 * @param {any} providerUser
 * @param {string} productKey
 * @param {any} subscription
 */
export function hasCurrentProductSubscription(
  providerUser,
  productKey,
  subscription = null
) {
  if (isProductSubscribed(providerUser, productKey)) return true;

  const status = String(subscription?.status || "").trim().toLowerCase();
  return Boolean(subscription?.id) && status !== "canceled";
}

/**
 * @param {any} providerUser
 * @param {string} productKey
 */
export function isProductVisible(providerUser, productKey) {
  const normalized = String(productKey || "").trim().toLowerCase();
  if (normalized === "care") {
    return !!(
      providerUser?.productAccess?.care?.visible ??
      providerUser?.productAccess?.care?.subscribed
    );
  }
  if (normalized === "booking") {
    return !!(
      providerUser?.productAccess?.booking?.visible ??
      providerUser?.productAccess?.booking?.subscribed
    );
  }
  if (normalized === "aivoice") {
    return !!(
      providerUser?.productAccess?.addons?.aiVoice?.visible ??
      providerUser?.productAccess?.addons?.aiVoice?.subscribed
    );
  }
  return false;
}
